import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getRewardTypes } from '../api/stelline';

const StellineRewardsContext = createContext(null);

const toPositiveIntegerOrNull = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const rounded = Math.trunc(parsed);
  return rounded > 0 ? rounded : null;
};

const toGameRewardMap = (rewardTypes) => {
  const map = {};
  if (!Array.isArray(rewardTypes)) {
    return map;
  }

  rewardTypes.forEach((rewardType) => {
    const gameType = typeof rewardType?.game_type === 'string' ? rewardType.game_type : null;
    if (!gameType) return;

    const variants = {};
    if (Array.isArray(rewardType.variants)) {
      rewardType.variants.forEach((variant) => {
        const key = typeof variant?.key === 'string' ? variant.key : null;
        const amount = toPositiveIntegerOrNull(variant?.amount);
        if (key && amount != null) {
          variants[key] = amount;
        }
      });
    }

    map[gameType] = {
      amount: toPositiveIntegerOrNull(rewardType.amount),
      maxAmount: toPositiveIntegerOrNull(rewardType.max_amount),
      variants,
    };
  });

  return map;
};

export const useStellineRewards = () => {
  const context = useContext(StellineRewardsContext);
  if (!context) {
    return {
      isLoading: false,
      rewardTypes: [],
      getGameWinReward: () => null,
      getGameWinMaxReward: () => null,
      getGameWinRewardRange: () => null,
      getOrtografiaDifficultyReward: () => null,
    };
  }
  return context;
};

export const StellineRewardsProvider = ({ children }) => {
  const { getToken } = useAuth();
  const [rewardTypes, setRewardTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadRewardTypes = async () => {
      try {
        const token = await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null);
        const data = await getRewardTypes(token);
        if (isMounted) {
          setRewardTypes(Array.isArray(data?.reward_types) ? data.reward_types : []);
        }
      } catch (error) {
        console.error('Failed to fetch stelline reward types:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadRewardTypes();

    return () => {
      isMounted = false;
    };
  }, [getToken]);

  const gameRewardMap = useMemo(() => toGameRewardMap(rewardTypes), [rewardTypes]);
  const getRangeForGameReward = (gameReward) => {
    if (!gameReward || typeof gameReward !== 'object') return null;

    const candidates = [];
    if (gameReward.amount != null) {
      candidates.push(gameReward.amount);
    }
    Object.values(gameReward.variants || {}).forEach((variantAmount) => {
      if (variantAmount != null) {
        candidates.push(variantAmount);
      }
    });

    if (candidates.length === 0) {
      return null;
    }

    return {
      min: Math.min(...candidates),
      max: Math.max(...candidates),
    };
  };

  const value = useMemo(() => ({
    isLoading,
    rewardTypes,
    getGameWinReward: (gameType) => {
      if (typeof gameType !== 'string') return null;
      return gameRewardMap[gameType]?.amount ?? null;
    },
    getGameWinMaxReward: (gameType) => {
      if (typeof gameType !== 'string') return null;
      const gameReward = gameRewardMap[gameType];
      if (!gameReward) return null;
      return gameReward.maxAmount ?? gameReward.amount ?? null;
    },
    getGameWinRewardRange: (gameType) => {
      if (typeof gameType !== 'string') return null;
      return getRangeForGameReward(gameRewardMap[gameType]);
    },
    getOrtografiaDifficultyReward: (difficulty) => {
      if (typeof difficulty !== 'string') return null;
      const normalizedDifficulty = difficulty.trim().toUpperCase();
      return gameRewardMap.ortografia?.variants?.[normalizedDifficulty] ?? null;
    },
  }), [gameRewardMap, isLoading, rewardTypes]);

  return (
    <StellineRewardsContext.Provider value={value}>
      {children}
    </StellineRewardsContext.Provider>
  );
};
